import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  CalendarBlank,
  ArrowsClockwise,
  TreeStructure,
  Prohibit,
  ChartBar,
  CheckCircle,
  Circle,
  Pencil,
  Tag,
  Users,
  CaretRight,
  ListBullets,
  Plus,
} from "@phosphor-icons/react";
import { iconForEvent } from "@/lib/event-icons";
import { getStatuses, findStatus, isDoneStatus, firstStatusIn, statusPillStyle, workstreamColor } from "@/lib/gantt-utils";
import { formatDate } from "@/lib/dates";

// Read-mostly task detail overlay matching the user's design:
//
//   ┌──────────────────────────────────┬──────────────────┐
//   │ Status · #N · workstream         │  Timeline        │
//   │ Title (h2)                       │  ──────────────  │
//   │ Description (editable)           │  Dependencies    │
//   │ Sub-tasks                        │  ──────────────  │
//   │   [✓] name              · status │  Workstream      │
//   │ Velocity bar                     │  ──────────────  │
//   └──────────────────────────────────┤  Markers & Risks │
//                                      └──────────────────┘
//
// - Description: click-to-edit textarea, persists via onUpdateTask({description})
// - Subtasks: checkbox toggles status Done ↔ To Do; Done = strike-through + green dot
// - Inline "+ Add subtask" at the bottom of the subtask list (rapid chip-add via parent)
// - Sidebar (right column) is scrollable on Y so Markers & Risks can grow without
//   pushing the main column.

export default function TaskDetailDialog({
  open,
  onOpenChange,
  task,
  allTasks,
  project,
  taskNumber,
  canEdit,
  onUpdateTask,
  onAddSubtask,
  onJumpToTask,
}) {
  const safeTask = task || {};
  const safeProject = project || {};
  const safeAll = allTasks || [];
  const statuses = getStatuses(safeProject);
  const statusDotFor = (s) => findStatus(statuses, s)?.color || "#94a3b8";

  const isSubtask = !!safeTask.parent_id;
  const parentTask = isSubtask ? safeAll.find((t) => t.id === safeTask.parent_id) : null;

  // Children of this task — empty for subtasks, list of nested rows for parents.
  const children = useMemo(
    () => safeAll
      .filter((t) => t.parent_id === safeTask.id)
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0)),
    [safeAll, safeTask.id],
  );

  // Resolve every dependency edge incident on this task. Includes:
  //   - parent (if subtask)
  //   - structured dependency_links (blocks vs blocked_by)
  //   - legacy free-text "dependencies" string (numeric → row index, else id match)
  //   - reverse edges from other tasks that target this task
  const depsByTarget = useMemo(() => {
    const out = { parent: parentTask, blockedBy: [], blocks: [] };
    (safeTask.dependency_links || []).forEach((l) => {
      const found = safeAll.find((t) => t.id === l.target_id);
      if (!found) return;
      if (l.kind === "blocks") out.blocks.push(found);
      else out.blockedBy.push(found);
    });
    (safeTask.dependencies || "")
      .split(/[,;\s]+/)
      .map((x) => x.trim())
      .filter(Boolean)
      .forEach((d) => {
        let found = null;
        const asNum = parseInt(d, 10);
        if (String(asNum) === d && asNum >= 1 && asNum <= safeAll.length) found = safeAll[asNum - 1];
        else found = safeAll.find((t) => t.id === d);
        if (found && !out.blockedBy.some((x) => x.id === found.id)) out.blockedBy.push(found);
      });
    safeAll.forEach((t) => {
      if (t.id === safeTask.id) return;
      (t.dependency_links || []).forEach((l) => {
        if (l.target_id !== safeTask.id) return;
        if (l.kind === "blocked_by" && !out.blocks.some((x) => x.id === t.id)) out.blocks.push(t);
      });
    });
    return out;
  }, [safeTask, safeAll, parentTask]);

  if (!task) return null;

  const completedChildren = children.filter((c) => isDoneStatus(statuses, c.status)).length;
  const childCompletion = children.length
    ? Math.round((completedChildren / children.length) * 100)
    : 0;

  const dateColumns = safeProject.date_columns || [];
  const dateEntries = dateColumns
    .map((c) => ({ col: c, value: safeTask.dates?.[c.id] || null }))
    .filter((e) => e.value);

  const customColumns = (safeProject.custom_columns || []).filter((c) => {
    const v = safeTask.custom_fields?.[c.id];
    return v !== undefined && v !== null && v !== "";
  });

  const events = safeTask.events || [];

  const wsColor = safeTask.workstream
    ? workstreamColor(safeTask.workstream, safeProject.workstream_colors || {})
    : null;
  const statusStyle = statusPillStyle(statuses, safeTask.status);
  const statusDot = statusDotFor(safeTask.status);

  const titleText = isSubtask
    ? safeTask.subtask || "Untitled subtask"
    : safeTask.task || "Untitled task";

  const toggleSubtaskDone = (subId, currentStatus) => {
    if (!canEdit || !onUpdateTask) return;
    const next = isDoneStatus(statuses, currentStatus)
      ? firstStatusIn(statuses, "not_started", "To Do")
      : firstStatusIn(statuses, "done", "Done");
    onUpdateTask(subId, { status: next });
  };

  const cycleStatus = (subId, currentStatus) => {
    if (!canEdit || !onUpdateTask) return;
    if (!statuses.length) return;
    const idx = statuses.findIndex((s) => s.label === currentStatus);
    const next = statuses[(idx + 1) % statuses.length].label;
    onUpdateTask(subId, { status: next });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-md max-w-3xl w-[92vw] max-h-[88vh] overflow-hidden flex flex-col p-0 gap-0"
        data-testid="task-detail-dialog"
      >
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b border-slate-200 dark:border-slate-700/40 shrink-0">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-mono-ibm uppercase tracking-widest font-semibold border`}
                  style={statusStyle}
                  data-testid="task-detail-status"
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: statusDot }} />
                  {safeTask.status || "No status"}
                </span>
                <span className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500">
                  Task #{taskNumber || "—"}
                </span>
                {safeTask.workstream && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono-ibm uppercase tracking-widest font-semibold"
                    style={{
                      background: wsColor?.overrideHex ? `${wsColor.overrideHex}1A` : "#f1f5f9",
                      color: wsColor?.overrideHex || "#475569",
                      border: `1px solid ${wsColor?.overrideHex ? `${wsColor.overrideHex}33` : "#cbd5e1"}`,
                    }}
                    data-testid="task-detail-workstream"
                  >
                    <Tag size={10} weight="fill" />
                    {safeTask.workstream}
                  </span>
                )}
                {isSubtask && parentTask && (
                  <button
                    type="button"
                    onClick={() => onJumpToTask?.(parentTask.id)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono-ibm uppercase tracking-widest text-slate-600 dark:text-slate-300 hover:text-slate-950 dark:hover:text-neon hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    data-testid="task-detail-jump-parent"
                    title="Jump to parent task"
                  >
                    <TreeStructure size={10} weight="bold" />
                    Subtask of "{parentTask.task}"
                  </button>
                )}
              </div>
              <DialogTitle className="font-display text-2xl sm:text-3xl font-black tracking-tight leading-tight text-slate-950 dark:text-mint pr-8">
                {titleText}
              </DialogTitle>
              <DialogDescription className="sr-only">Task detail view</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Body — main column scrolls; sidebar scrolls independently */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_300px] min-h-0">
          {/* Main column */}
          <div className="overflow-auto custom-scrollbar p-6 space-y-6 min-w-0">
            {/* Description (blue circle in user design) */}
            <DescriptionBlock
              value={safeTask.description || ""}
              canEdit={!!canEdit}
              onSave={(v) => onUpdateTask?.(safeTask.id, { description: v })}
            />

            {/* Sub-tasks (green circle) — only for parent tasks (not on a subtask itself) */}
            {!isSubtask && (
              <Section
                icon={<ListBullets size={12} weight="bold" />}
                label="Sub-tasks"
                count={children.length > 0 ? `${completedChildren} / ${children.length} complete` : null}
                testid="task-detail-subtasks-section"
              >
                {children.length === 0 ? (
                  <div className="text-[12px] text-slate-500 dark:text-slate-400 italic mb-3">
                    No sub-tasks yet — add the first one below.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {children.map((c) => {
                      const cs = statusPillStyle(statuses, c.status);
                      const csDot = statusDotFor(c.status);
                      const done = isDoneStatus(statuses, c.status);
                      return (
                        <div
                          key={c.id}
                          className="flex items-center gap-3 p-2.5 rounded-md border border-slate-200 dark:border-slate-700/40 hover:border-slate-950 dark:hover:border-neon hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors group"
                          data-testid={`task-detail-subtask-${c.id}`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleSubtaskDone(c.id, c.status)}
                            disabled={!canEdit}
                            className="shrink-0"
                            title={done ? "Mark as not started" : "Mark as done"}
                            data-testid={`task-detail-subtask-toggle-${c.id}`}
                          >
                            {done ? (
                              <CheckCircle size={18} weight="fill" className="text-emerald-500" />
                            ) : (
                              <Circle size={18} weight="regular" className="text-slate-300 dark:text-slate-600 hover:text-slate-500" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => onJumpToTask?.(c.id)}
                            className="flex-1 text-left min-w-0"
                            data-testid={`task-detail-subtask-jump-${c.id}`}
                          >
                            <span
                              className={`text-sm truncate block ${
                                done ? "line-through text-slate-400" : "text-slate-800 dark:text-slate-100"
                              }`}
                            >
                              {c.subtask || "Untitled subtask"}
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => cycleStatus(c.id, c.status)}
                            disabled={!canEdit}
                            className={`px-2 py-0.5 rounded text-[10px] font-mono-ibm uppercase tracking-widest shrink-0 border hover:opacity-80 transition-opacity inline-flex items-center gap-1`}
                            style={cs}
                            title={canEdit ? "Click to cycle status" : ""}
                            data-testid={`task-detail-subtask-status-${c.id}`}
                          >
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: csDot }} />
                            {c.status || "No status"}
                          </button>
                          <CaretRight size={11} weight="bold" className="text-slate-300 group-hover:text-slate-950 dark:group-hover:text-neon transition-colors shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                )}

                {canEdit && onAddSubtask && (
                  <InlineAddSubtask
                    onAdd={(names) => onAddSubtask(safeTask.id, names)}
                    testid="task-detail-add-subtask"
                  />
                )}

                {/* Velocity bar — shown only when there are children */}
                {children.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500 mb-1.5">
                      <span>Task velocity</span>
                      <span className="font-semibold text-slate-950 dark:text-mint">{childCompletion}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden" data-testid="task-detail-velocity">
                      <div
                        className="h-full bg-slate-950 dark:bg-neon transition-all"
                        style={{ width: `${childCompletion}%` }}
                      />
                    </div>
                  </div>
                )}
              </Section>
            )}

            {/* Custom columns (kept in main column — they're task-level data) */}
            {customColumns.length > 0 && (
              <Section
                icon={<Pencil size={12} weight="bold" />}
                label="Custom fields"
                testid="task-detail-custom-section"
              >
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {customColumns.map((c) => (
                    <div
                      key={c.id}
                      className="rounded-md border border-slate-200 dark:border-slate-700/40 p-3"
                      data-testid={`task-detail-custom-${c.id}`}
                    >
                      <dt className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500">{c.label}</dt>
                      <dd className="text-sm text-slate-950 dark:text-mint mt-1 break-words">
                        {String(safeTask.custom_fields[c.id])}
                      </dd>
                    </div>
                  ))}
                </dl>
              </Section>
            )}
          </div>

          {/* Right sidebar — independently scrollable so Markers & Risks (orange) can grow */}
          <aside className="overflow-auto custom-scrollbar border-t lg:border-t-0 lg:border-l border-slate-200 dark:border-slate-700/40 bg-slate-50/40 dark:bg-slate-900/30 p-6 space-y-6">
            {/* Timeline */}
            <SidebarBlock label="Timeline" icon={<CalendarBlank size={12} weight="bold" />} testid="task-detail-timeline">
              {dateEntries.length === 0 ? (
                <p className="text-[12px] text-slate-400 italic">No dates set yet.</p>
              ) : (
                <ul className="space-y-2">
                  {dateEntries.map((e) => (
                    <li
                      key={e.col.id}
                      className="flex items-start justify-between gap-2"
                      data-testid={`task-detail-date-${e.col.id}`}
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="h-2 w-2 rounded-full shrink-0"
                          style={{ background: e.col.color }}
                        />
                        <span className="text-[11px] text-slate-600 dark:text-slate-300 truncate">{e.col.label}</span>
                      </span>
                      <span className="text-[11px] font-mono-ibm text-slate-950 dark:text-mint shrink-0">
                        {formatDate(e.value)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </SidebarBlock>

            {/* Dependencies */}
            <SidebarBlock label="Dependencies" icon={<ArrowsClockwise size={12} weight="bold" />} testid="task-detail-deps">
              {!depsByTarget.parent && depsByTarget.blockedBy.length === 0 && depsByTarget.blocks.length === 0 ? (
                <p className="text-[12px] text-slate-400 italic">No dependencies.</p>
              ) : (
                <div className="space-y-2.5">
                  {depsByTarget.parent && (
                    <DepRow
                      kindLabel="Parent"
                      kindColor="text-slate-500"
                      icon={<TreeStructure size={11} weight="bold" />}
                      target={depsByTarget.parent}
                      onJump={onJumpToTask}
                      testid={`task-detail-dep-parent-${depsByTarget.parent.id}`}
                    />
                  )}
                  {depsByTarget.blockedBy.map((t) => (
                    <DepRow
                      key={`bb-${t.id}`}
                      kindLabel="Blocked by"
                      kindColor="text-rose-600 dark:text-rose-400"
                      icon={<Prohibit size={11} weight="bold" />}
                      target={t}
                      onJump={onJumpToTask}
                      testid={`task-detail-dep-blockedby-${t.id}`}
                    />
                  ))}
                  {depsByTarget.blocks.map((t) => (
                    <DepRow
                      key={`bk-${t.id}`}
                      kindLabel="Blocks"
                      kindColor="text-amber-600 dark:text-amber-400"
                      icon={<Prohibit size={11} weight="bold" />}
                      target={t}
                      onJump={onJumpToTask}
                      testid={`task-detail-dep-blocks-${t.id}`}
                    />
                  ))}
                </div>
              )}
            </SidebarBlock>

            {/* Workstream */}
            <SidebarBlock label="Workstream" icon={<Users size={12} weight="bold" />} testid="task-detail-workstream-block">
              {safeTask.workstream ? (
                <div className="flex items-center gap-2">
                  <span
                    className="h-7 w-7 rounded-md flex items-center justify-center text-white font-semibold text-xs"
                    style={{ background: wsColor?.overrideHex || "#475569" }}
                  >
                    {safeTask.workstream.charAt(0).toUpperCase()}
                  </span>
                  <span className="text-sm text-slate-800 dark:text-slate-100">{safeTask.workstream}</span>
                </div>
              ) : (
                <p className="text-[12px] text-slate-400 italic">Unassigned.</p>
              )}
            </SidebarBlock>

            {/* Markers & Risks (orange circle in design) */}
            <SidebarBlock
              label={`Markers & Labels${events.length > 0 ? ` · ${events.length}` : ""}`}
              icon={<ChartBar size={12} weight="bold" />}
              testid="task-detail-events-section"
            >
              {events.length === 0 ? (
                <p className="text-[12px] text-slate-400 italic">No markers yet.</p>
              ) : (
                <div className="space-y-2">
                  {events.map((ev) => {
                    const meta = iconForEvent(ev);
                    const Icon = meta.icon;
                    const color = ev.color || meta.color;
                    const label = ev.icon_label || meta.label;
                    return (
                      <div
                        key={ev.id}
                        className="flex items-start gap-2 p-2.5 rounded-md border border-slate-200 dark:border-slate-700/40 bg-white dark:bg-slate-900/40"
                        data-testid={`task-detail-event-${ev.id}`}
                      >
                        <div
                          className="h-6 w-6 rounded-md flex items-center justify-center shrink-0 mt-0.5"
                          style={{ background: color }}
                        >
                          <Icon size={12} weight="fill" className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] font-mono-ibm uppercase tracking-widest font-semibold" style={{ color }}>
                              {label}
                            </span>
                            {ev.event_date && (
                              <span className="text-[10px] font-mono-ibm text-slate-400">
                                {formatDate(ev.event_date)}
                              </span>
                            )}
                          </div>
                          {ev.comment && (
                            <p className="text-[12px] text-slate-700 dark:text-slate-200 leading-relaxed mt-1 whitespace-pre-wrap break-words">
                              {ev.comment}
                            </p>
                          )}
                          {ev.author_email && (
                            <div className="text-[10px] font-mono-ibm text-slate-400 mt-1">— {ev.author_email}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </SidebarBlock>
          </aside>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 dark:border-slate-700/40 px-6 py-3 flex items-center justify-between gap-3 shrink-0 bg-white dark:bg-[#15101a]">
          <div className="text-[11px] font-mono-ibm uppercase tracking-widest text-slate-400">
            Updated {safeTask.updated_at ? formatDate(safeTask.updated_at) : "—"}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-md border-slate-300 h-9"
              data-testid="task-detail-close-btn"
            >
              Close
            </Button>
            <Button
              onClick={() => onOpenChange(false)}
              className="rounded-md bg-slate-950 hover:bg-slate-800 dark:bg-neon dark:hover:bg-[#b8de00] text-white dark:text-ink h-9"
              data-testid="task-detail-edit-btn"
              title="Edit fields directly in the row"
            >
              <Pencil size={12} weight="bold" />
              <span className="ml-1.5">Edit in row</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- description editor ----------

function DescriptionBlock({ value, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const ref = useRef(null);

  useEffect(() => {
    setVal(value || "");
  }, [value]);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      ref.current.setSelectionRange(ref.current.value.length, ref.current.value.length);
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if ((val || "") !== (value || "")) onSave?.(val);
  };

  return (
    <section data-testid="task-detail-description-section">
      <header className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500">
          <span className="h-5 w-5 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-mint">
            <Pencil size={11} weight="bold" />
          </span>
          Description
        </div>
        {!editing && canEdit && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-400 hover:text-slate-950 dark:hover:text-neon"
            data-testid="task-detail-description-edit"
          >
            {value ? "Edit" : "Add"}
          </button>
        )}
      </header>
      {editing ? (
        <textarea
          ref={ref}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setVal(value || "");
              setEditing(false);
            }
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              commit();
            }
          }}
          rows={4}
          placeholder="Add a description — context, acceptance criteria, links…"
          className="w-full text-sm text-slate-900 dark:text-mint bg-transparent outline-none border border-slate-300 dark:border-slate-700 rounded-md p-2.5 leading-relaxed resize-none focus:border-slate-950 dark:focus:border-neon"
          data-testid="task-detail-description-input"
        />
      ) : value ? (
        <button
          type="button"
          onClick={() => canEdit && setEditing(true)}
          disabled={!canEdit}
          className="w-full text-left text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap break-words border border-transparent hover:border-slate-200 dark:hover:border-slate-700 rounded-md p-2.5 -m-2.5 transition-colors"
          data-testid="task-detail-description"
        >
          {value}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => canEdit && setEditing(true)}
          disabled={!canEdit}
          className="w-full text-left text-[12px] italic text-slate-400 border border-dashed border-slate-300 dark:border-slate-700 rounded-md p-2.5 hover:border-slate-950 dark:hover:border-neon transition-colors"
          data-testid="task-detail-description-empty"
        >
          {canEdit ? "Click to add a description…" : "No description."}
        </button>
      )}
    </section>
  );
}

// Inline subtask add row used at the bottom of the subtasks section. Simple
// rapid-fire input — Enter adds AND keeps the input open so the user can chain.
function InlineAddSubtask({ onAdd, testid }) {
  const [val, setVal] = useState("");
  const ref = useRef(null);

  const commit = () => {
    const name = val.trim();
    if (!name) return;
    onAdd([name]);
    setVal("");
    requestAnimationFrame(() => ref.current?.focus());
  };

  return (
    <div className="mt-3 flex items-center gap-2" data-testid={testid}>
      <span className="h-5 w-5 rounded-md border border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center text-slate-400 shrink-0">
        <Plus size={11} weight="bold" />
      </span>
      <input
        ref={ref}
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
        placeholder="Add a sub-task — Enter to add another"
        className="flex-1 h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-mint outline-none focus:border-slate-950 dark:focus:border-neon placeholder:text-slate-400 dark:placeholder:text-slate-600"
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={commit}
        disabled={!val.trim()}
        className="h-8 px-3 text-[11px] font-medium rounded-md bg-slate-950 text-white hover:bg-slate-800 dark:bg-neon dark:text-ink dark:hover:bg-[#b8de00] disabled:opacity-40"
        data-testid={`${testid}-commit`}
      >
        Add
      </button>
    </div>
  );
}

// ---------- internal layout helpers ----------

function Section({ icon, label, count, children, testid }) {
  return (
    <section data-testid={testid}>
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500">
          <span className="h-5 w-5 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-mint">
            {icon}
          </span>
          {label}
        </div>
        {count && (
          <span className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-400">{count}</span>
        )}
      </header>
      {children}
    </section>
  );
}

function SidebarBlock({ label, icon, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500 mb-2">
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}

function DepRow({ kindLabel, kindColor, icon, target, onJump, testid }) {
  const title = target.task || target.subtask || "Untitled";
  return (
    <button
      type="button"
      onClick={() => onJump?.(target.id)}
      className="w-full flex items-center gap-2 text-left group"
      data-testid={testid}
    >
      <span className={`flex items-center gap-1 text-[9px] font-mono-ibm uppercase tracking-widest font-semibold shrink-0 ${kindColor}`}>
        {icon}
        {kindLabel}
      </span>
      <span className="flex-1 text-[12px] text-slate-700 dark:text-slate-200 group-hover:text-slate-950 dark:group-hover:text-neon truncate transition-colors">
        {title}
      </span>
      <CaretRight size={10} weight="bold" className="text-slate-300 group-hover:text-slate-950 dark:group-hover:text-neon transition-colors shrink-0" />
    </button>
  );
}
